import { LanguagesEnum } from '@/api/enums';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { t } from 'i18next';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledFlexContainer = styled(FlexContainer)`
  margin-top: auto;
  margin-left: auto;
  max-width: 1000px;

  .nav {
    border-radius: 50%;
    width: 48px;
    height: 48px;
  }
`;

type SlidesNavigationProps = {
  className?: string;
  currentSlideIdx: number;
  totalSlides: number;
  isEditingPopUpSlide: boolean;
  isEditMode?: boolean;
  hasNextModule?: boolean;
  hasPreviousModule?: boolean;
  attemptLanguage?: LanguagesEnum;
  rtl?: boolean;
  continueOrFinishButton?: React.ReactNode;
  onPrevSlide: () => void;
  onNextSlide: () => void;
  onLastPageReached?: () => void;
};

export const SlidesNavigation: React.FC<SlidesNavigationProps> = ({
  className,
  currentSlideIdx,
  totalSlides,
  isEditingPopUpSlide,
  isEditMode,
  hasNextModule,
  hasPreviousModule,
  attemptLanguage,
  rtl,
  continueOrFinishButton,
  onPrevSlide,
  onNextSlide,
  onLastPageReached,
}) => {
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);

  const minSwipeDistance = 50;
  const minHorizontalRatio = 2;

  // Keyboard navigation
  useEffect(() => {
    if (isEditMode) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (currentSlideIdx > 0 || hasPreviousModule) {
            onPrevSlide();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (currentSlideIdx < totalSlides - 1) {
            onNextSlide();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    currentSlideIdx,
    totalSlides,
    hasPreviousModule,
    isEditMode,
    onPrevSlide,
    onNextSlide,
  ]);

  // Touch navigation
  useEffect(() => {
    if (isEditMode) return;

    const handleTouchStart = (event: TouchEvent) => {
      touchStartX.current = event.touches[0].clientX;
      touchStartY.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      touchEndX.current = event.touches[0].clientX;
      touchEndY.current = event.touches[0].clientY;
    };

    const handleTouchEnd = () => {
      if (isEditMode) return;

      if (
        !touchStartX.current ||
        !touchEndX.current ||
        !touchStartY.current ||
        !touchEndY.current
      )
        return;

      const horizontalDistance = touchStartX.current - touchEndX.current;
      const verticalDistance = touchStartY.current - touchEndY.current;

      const isHorizontalSwipe =
        Math.abs(horizontalDistance) >
        Math.abs(verticalDistance) * minHorizontalRatio;

      if (
        isHorizontalSwipe &&
        horizontalDistance > minSwipeDistance &&
        currentSlideIdx < totalSlides - 1
      ) {
        onNextSlide();
      } else if (
        isHorizontalSwipe &&
        horizontalDistance < -minSwipeDistance &&
        currentSlideIdx > 0
      ) {
        onPrevSlide();
      }

      touchStartX.current = null;
      touchEndX.current = null;
      touchStartY.current = null;
      touchEndY.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentSlideIdx, totalSlides, isEditMode, onPrevSlide, onNextSlide]);

  useEffect(() => {
    if (currentSlideIdx === totalSlides - 1 && onLastPageReached) {
      onLastPageReached();
    }
  }, [currentSlideIdx, totalSlides, onLastPageReached]);

  return (
    <>
      {!isEditingPopUpSlide && totalSlides > 0 && (
        <StyledFlexContainer
          justify="flex-end"
          align="center"
          gap={16}
          className={className}
        >
          {currentSlideIdx !== totalSlides - 1 && (
            <>
              <AppButton
                icon={`pi ${rtl ? 'pi-arrow-right' : 'pi-arrow-left'}`}
                type="outlined"
                ariaLabel={
                  attemptLanguage
                    ? hardcodedTranslations.buttons.previous[attemptLanguage]
                    : t('button.previous')
                }
                isDisabled={currentSlideIdx === 0 && !hasPreviousModule}
                onClick={onPrevSlide}
              />
              <AppButton
                icon={`pi ${rtl ? 'pi-arrow-left' : 'pi-arrow-right'}`}
                ariaLabel={
                  attemptLanguage
                    ? hardcodedTranslations.buttons.next[attemptLanguage]
                    : t('button.next')
                }
                isDisabled={
                  currentSlideIdx === totalSlides - 1 && !hasNextModule
                }
                onClick={onNextSlide}
              />
            </>
          )}
          {currentSlideIdx === totalSlides - 1 && (
            <div className={rtl ? 'mr-auto' : 'ml-auto'}>
              {continueOrFinishButton}
            </div>
          )}
        </StyledFlexContainer>
      )}
    </>
  );
};
